import React, { useEffect, useState } from "react";
import api from "../../pages/chat/api";
import { toast } from "../../utils/appToast";

const CreateTagModals = ({
  isOpen,
  onClose,
  documentId,
  onTagUpdate,
  onClearSelection,
}) => {
  const [allTags, setAllTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [fieldName, setFieldName] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [fieldPrompt, setFieldPrompt] = useState("");
  const isCreatingTag = fieldName.trim() !== "" || selectedColor !== "";

  const colorOptions = [
    "#D8FF75",
    "#7FFF7A",
    "#7AFFCE",
    "#7AEDFF",
    "#7ABEFF",
    "#7A9FFF",
    "#B27AFF",
    "#E97AFF",
    "#FF7AD7",
    "#FF7AA5",
    "#FF7A7A",
    "#FFFFFF",
    "#F7F7F7",
  ];

  const [isLoading, setIsLoading] = useState(false);

  const fetchTags = async () => {
    const docIds = Array.isArray(documentId) ? documentId : [documentId];
    if (docIds.length === 0) return;

    setIsLoading(true);
    try {
      const [allTagsRes, ...appliedResList] = await Promise.all([
        api.get("/api/tagging/tags/"),
        ...docIds.map((id) => api.get(`/api/tagging/documents/${id}/tags/`)),
      ]);

      // ✅ Merge all applied tag IDs across selected documents
      const appliedTagIds = new Set();
      appliedResList.forEach((res) => {
        res.data.forEach((tag) => appliedTagIds.add(tag.id));
      });

      setAllTags(allTagsRes.data);
      setSelectedTagIds(Array.from(appliedTagIds));
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to create tag. Please try again.";

      toast.error(msg, {
        position: "top-right",
        autoClose: 4000,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && documentId) {
      setAllTags([]);
      setSelectedTagIds([]);
      setSearchTerm("");
      fetchTags();
    }
  }, [isOpen, documentId]);

  const toggleTag = (tagId) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleAddField = async () => {
    if (!fieldName.trim() || !selectedColor) return;

    try {
      const res = await api.post("/api/tagging/tags/", {
        name: fieldName.trim(),
        color: selectedColor,
        description: fieldPrompt.trim() || "",
      });
      const newTag = res.data;
      setAllTags((prev) => [...prev, newTag]);
      setSelectedTagIds((prev) => [...prev, newTag.id]);
      setFieldName("");
      setFieldPrompt("");
      setSelectedColor("");
      toast.success("Tag created!");
    } catch (err) {
      toast.error("Failed to create tag.");
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApplyTags = async () => {
    const docIds = Array.isArray(documentId) ? documentId : [documentId];
    setIsSubmitting(true); // Start loading

    try {
      await Promise.all(
        docIds.map((id) =>
          api.post(`/api/tagging/documents/${id}/add_tags/`, {
            tag_ids: selectedTagIds,
          })
        )
      );

      toast.success("Tags applied successfully");
      onTagUpdate?.(); // Refresh parent data
      onClearSelection?.(); // Reset checkboxes in parent (✅)
      onClose(); // Close modal
    } catch (err) {
      console.error("Failed to apply tags", err);
      toast.error("Failed to apply tags");
    } finally {
      setIsSubmitting(false); // Stop loading
    }
  };

  const renderTagPill = (tag, actionSymbol) => (
    <div
      key={tag.id}
      onClick={() => toggleTag(tag.id)}
      className="tag-pill cursor-pointer flex items-center gap-1"
      style={{
        color: darkenColor(tag.color),
        backgroundColor: `${tag.color}1A`,
        border: `2px solid ${tag.color}`,
      }}
    >
      <span>{tag.name}</span>
      {actionSymbol && <span className="ml-1">{actionSymbol}</span>}
    </div>
  );

  const filteredTags = allTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  function darkenColor(hex, amount = 0.4) {
    if (!hex || !hex.startsWith("#")) return "#333";
    const num = parseInt(hex.slice(1), 16);
    let r = (num >> 16) & 255;
    let g = (num >> 8) & 255;
    let b = num & 255;

    r = Math.floor(r * (1 - amount));
    g = Math.floor(g * (1 - amount));
    b = Math.floor(b * (1 - amount));

    return `rgb(${r}, ${g}, ${b})`;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center">
      <div className="relative w-[445px] min-h-[357px] max-h-[90vh] p-4 pb-20 bg-white rounded-[12px] border border-black/10 overflow-y-auto shadow-[153px_261px_85px_rgba(0,0,0,0),98px_167px_77px_rgba(0,0,0,0.01),55px_94px_65px_rgba(0,0,0,0.05),24px_42px_48px_rgba(0,0,0,0.09),6px_10px_27px_rgba(0,0,0,0.10)]">
        {/* Close button */}
        <button
          className="absolute top-2 right-4 text-[16px]"
          onClick={onClose}
        >
          <span className="text-[#73607B] font-[Outfit] text-[16px] font-medium leading-none">
            x
          </span>
        </button>

        {/* Title */}

        <h2 className="flex">
          <img src="/files.svg" alt="search" />
          <span className="text-[#171717] font-[Outfit] text-[16px] font-semibold leading-none mt-1">
            Apply Tags to Selected Documents{" "}
          </span>
          <span className="text-[#73607B] font-[Outfit] text-[14px] font-medium leading-none mt-1">
            ({Array.isArray(documentId) ? documentId.length : 1} selected)
          </span>
        </h2>
        {/* ✅ Selected Tags — move this above the search bar */}
        {selectedTagIds.length > 0 && (
          <div className="mb-2 mt-2 flex flex-wrap gap-2">
            {selectedTagIds.map((id) => {
              const tag = allTags.find((t) => t.id === id);
              return tag ? renderTagPill(tag, "×") : null;
            })}
          </div>
        )}

        <div className="mt-2 flex items-center gap-[10px] px-[16px] pr-[44px] py-[10px] border border-[rgba(0,0,0,0.10)] rounded-[24px] w-full mb-3">
          <img src="/search.svg" alt="Search" className="w-6 h-6" />
          <input
            type="text"
            className="flex-1 font-[Outfit] text-[14px] text-[rgba(82,82,91,0.72)] 
             placeholder-[rgba(82,82,91,0.72)] placeholder:font-[Outfit] 
             placeholder:text-[14px] placeholder:font-normal placeholder:leading-none 
             outline-none border-none focus:outline-none focus:ring-0 bg-transparent"
            placeholder="Search or create tag…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Tags list */}
        {/* ✅ Selected Tags */}
        {/* ✅ Selected Tags */}
        {isLoading ? (
          <div className="text-sm text-gray-500 mb-2">Loading tags...</div>
        ) : (
          <>
            {/* ✅ Selected Tags */}
            {/* {selectedTagIds.length > 0 && (
  <div className="mb-2 flex flex-wrap gap-2">
    {selectedTagIds.map((id) => {
      const tag = allTags.find((t) => t.id === id);
      return tag ? renderTagPill(tag, "×") : null;
    })}
  </div>
)} */}

            {/* 🔻 Label */}
            <div className="self-stretch text-[#52525B] font-[Outfit] text-[15px] font-medium leading-none mt-2">
              Add tags
            </div>

            {/* ✅ Available Tags */}
            <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto pr-1 mt-2">
              {filteredTags
                .filter((tag) => !selectedTagIds.includes(tag.id))
                .map((tag) => renderTagPill(tag, "+"))}
            </div>
          </>
        )}

        {/* Create tag section */}
        <details className="pt-2 text-sm group">
          <summary className="flex items-center justify-between px-3 py-1 w-[147px] h-[28px] rounded-full bg-[#8B55DD] text-white font-[Outfit] text-[14px] font-medium cursor-pointer select-none transition-all">
            Create New tag
            <img
              src="/arrow-down.svg"
              alt="Toggle"
              className="w-4 h-4 transform transition-transform duration-200 ease-in-out group-open:rotate-180"
            />
          </summary>

          <div className="mt-3">
            <label className="block text-[#52525B] font-[Outfit] text-[13px] font-medium leading-none mb-1">
              Name
            </label>{" "}
            <input
              type="text"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              placeholder="Eg: Contracts"
              className="w-[403px] h-[40px] border border-[rgba(0,0,0,0.10)] rounded-[6px] px-3 py-2 mb-3 text-[13px] font-[Outfit] text-[rgba(82,82,91,0.64)] placeholder:text-[rgba(82,82,91,0.64)] placeholder:text-[13px] placeholder:font-[Outfit] placeholder:font-normal placeholder:leading-none"
            />
            <label className="block text-[#52525B] font-[Outfit] text-[13px] font-medium leading-none mb-1">
              Color
            </label>{" "}
            <div className="flex flex-wrap gap-2 mb-3">
              {colorOptions.map((color, i) => (
                <div
                  key={i}
                  onClick={() => setSelectedColor(color)}
                  className={`w-6 h-6 rounded-md border cursor-pointer`}
                  style={{
                    backgroundColor: color,
                    outline:
                      selectedColor === color ? "2px solid black" : "none",
                  }}
                />
              ))}
            </div>
            <label className="block text-[#52525B] font-[Outfit] text-[13px] font-medium leading-none mb-1">
              Description{" "}
              <span className="text-xs text-gray-400">(optional)</span>
            </label>
            <textarea
              rows={2}
              value={fieldPrompt}
              onChange={(e) => setFieldPrompt(e.target.value)}
              className="w-[403px] h-[80px] border border-[rgba(0,0,0,0.10)] rounded-[6px] px-3 py-2 text-[13px] font-[Outfit] text-[rgba(82,82,91,0.64)] placeholder:text-[rgba(82,82,91,0.64)] placeholder:text-[13px] placeholder:font-[Outfit] placeholder:font-normal placeholder:leading-none resize-none"
              placeholder="Eg: All the Contracts Signed by the company"
            />
          </div>
        </details>

        {/* Done Button */}
        <div className="absolute bottom-4 right-5">
          {isCreatingTag ? (
            <button
              onClick={handleAddField}
              disabled={!fieldName.trim() || !selectedColor}
              className="flex items-center justify-center gap-2 px-[40px] py-[10px] rounded-[6px] bg-[#C95EBE] text-white font-[Outfit] text-[14px] font-medium disabled:opacity-50 transition"
            >
              Create Tag
            </button>
          ) : (
            <button
              onClick={handleApplyTags}
              disabled={isSubmitting}
              className="flex items-center justify-center gap-2 px-[40px] py-[10px] rounded-[6px] bg-[#C95EBE] text-white font-[Outfit] text-[14px] font-medium disabled:opacity-50 transition"
            >
              {isSubmitting ? "Adding..." : "Done"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateTagModals;
